import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Mail, ExternalLink, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PantriesPage() {
  const { t } = useTranslation();
  const [pantries, setPantries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPantries = async () => {
      try {
        const response = await axios.get(`${API}/pantries`);
        setPantries(response.data.filter(p => p.is_active));
      } catch (error) {
        console.error('Failed to load pantries');
      } finally {
        setLoading(false);
      }
    };
    fetchPantries();
  }, []);

  return (
    <div className="min-h-screen bg-cream" data-testid="pantries-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-olive to-olive/90">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Food Pantry Locations
            </h1>
            <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
              Living Fruit Ministry serves the community at various locations throughout Central Florida
            </p>
          </motion.div>
        </div>
      </section>

      {/* Weekly Update Banner */}
      <section className="bg-gold/20 border-b border-gold/30">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="text-burgundy flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-charcoal font-medium text-lg">
                  Check Back Weekly for Updates!
                </p>
                <p className="text-charcoal/70">
                  Our pantry locations and schedules may change week to week. Please check this page before visiting.
                </p>
              </div>
            </div>
            <Link
              to="/resources"
              className="flex-shrink-0 px-6 py-3 bg-burgundy text-white rounded-full font-medium hover:bg-purple transition-colors"
            >
              View All Community Resources
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Pantry Locations */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : pantries.length === 0 ? (
            <div className="text-center py-16">
              <MapPin size={64} className="mx-auto text-charcoal/20 mb-4" />
              <h2 className="font-serif text-2xl text-charcoal mb-2">No Locations Available</h2>
              <p className="text-charcoal/60 max-w-md mx-auto">
                Please check back soon for updated pantry locations and schedules. 
                Contact us for more information about our food distribution.
              </p>
              <Link
                to="/contact"
                className="inline-block mt-6 px-6 py-3 bg-burgundy text-white rounded-full font-medium hover:bg-purple transition-colors"
              >
                Contact Us
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {pantries.map((pantry, index) => (
                <motion.div
                  key={pantry.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 border border-gold/10 hover:border-gold/30 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-olive/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin size={28} className="text-olive" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-xl text-charcoal mb-2">{pantry.name}</h3>
                      
                      <div className="space-y-2 text-sm">
                        <p className="flex items-start gap-2 text-charcoal/70">
                          <MapPin size={16} className="flex-shrink-0 mt-0.5" />
                          {pantry.address}
                        </p>
                        
                        {pantry.schedule && (
                          <p className="flex items-start gap-2 text-charcoal/70">
                            <Clock size={16} className="flex-shrink-0 mt-0.5" />
                            {pantry.schedule}
                          </p>
                        )}
                        
                        {pantry.phone && (
                          <p className="flex items-center gap-2 text-charcoal/70">
                            <Phone size={16} />
                            <a href={`tel:${pantry.phone}`} className="hover:text-burgundy">{pantry.phone}</a>
                          </p>
                        )}
                        
                        {pantry.contact_email && (
                          <p className="flex items-center gap-2 text-charcoal/70">
                            <Mail size={16} />
                            <a href={`mailto:${pantry.contact_email}`} className="hover:text-burgundy">{pantry.contact_email}</a>
                          </p>
                        )}
                      </div>
                      
                      {pantry.special_notes && (
                        <div className="mt-4 p-3 bg-gold/10 rounded-lg">
                          <p className="text-sm text-charcoal/70 italic">{pantry.special_notes}</p>
                        </div>
                      )}
                      
                      {pantry.maps_url && (
                        <a
                          href={pantry.maps_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-olive/10 text-olive rounded-lg text-sm font-medium hover:bg-olive hover:text-white transition-colors"
                        >
                          <ExternalLink size={14} /> Get Directions
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Need Food Now? */}
      <section className="py-12 bg-burgundy/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-charcoal mb-4">
            Need Food Assistance Now?
          </h2>
          <p className="text-charcoal/70 mb-6">
            If you need immediate food assistance, we can help connect you with additional resources in your area.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/resources"
              className="px-6 py-3 bg-burgundy text-white rounded-full font-medium hover:bg-purple transition-colors"
            >
              View Community Resources
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 border-2 border-burgundy text-burgundy rounded-full font-medium hover:bg-burgundy hover:text-white transition-colors"
            >
              Contact Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
